import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, CheckCircle2, Copy, ExternalLink, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, documentPdfUrl, type ExactCustomer, type ExactPaymentStatus, type VerkoopProposal } from '@/lib/api';
import { EXACT_STATUS_LABELS, PAYMENT_STATUS_DOT, PAYMENT_STATUS_LABELS } from '@/lib/labels';
import {
	approveVerkoop,
	deleteDocument,
	patchVerkoopProposal,
	rejectVerkoop,
	reopenVerkoop,
} from '@/lib/mutations';
import { formatDateISO } from '@/lib/utils';

export const Route = createFileRoute('/verkoopfacturen/$id')({
	component: VerkoopApprovalPage,
});

function VerkoopApprovalPage() {
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
	const docQuery = useQuery({ queryKey: ['verkoopfactuur', id], queryFn: () => api.verkoopfactuur(id) });

	const doc = docQuery.data?.document as
		| (Record<string, unknown> & {
				entity_code: string;
				entity_name: string;
				mime_type?: string;
				original_filename?: string;
				exact_status?: string;
				exact_error_reason?: string | null;
			})
		| undefined;
	const proposal = docQuery.data?.proposal ?? null;
	const entityCode = doc?.entity_code ?? '';

	const customersQuery = useQuery({
		queryKey: ['exact-customers', entityCode],
		queryFn: () => api.exactCustomers(entityCode),
		enabled: Boolean(entityCode),
	});

	const [draft, setDraft] = useState<VerkoopProposal | null>(null);
	useEffect(() => {
		if (proposal) setDraft(structuredClone(proposal));
	}, [proposal]);

	const [error, setError] = useState<string | null>(null);

	const pendingListQuery = useQuery({
		queryKey: ['verkoopfacturen', 'pending_for_next', doc?.entity_code],
		queryFn: () =>
			api.verkoopfacturen({
				entity_code: doc?.entity_code,
				exact_status: 'pending_review',
				limit: 50,
			}),
		enabled: Boolean(doc?.entity_code),
	});

	const goToNextPending = () => {
		const items = pendingListQuery.data?.items ?? [];
		const next = items.find((item) => item.id !== id);
		if (next) {
			navigate({ to: '/verkoopfacturen/$id', params: { id: next.id } });
		} else {
			navigate({ to: '/verkoopfacturen' });
		}
	};

	const invalidate = () => {
		queryClient.invalidateQueries({ queryKey: ['verkoopfactuur', id] });
		queryClient.invalidateQueries({ queryKey: ['verkoopfacturen'] });
	};

	const patchMutation = useMutation({
		mutationFn: (next: VerkoopProposal) => patchVerkoopProposal(id, next),
		onSuccess: () => invalidate(),
		onError: handleError,
	});
	const approveMutation = useMutation({
		mutationFn: async () => {
			if (draft) await patchVerkoopProposal(id, draft);
			return approveVerkoop(id);
		},
		onSuccess: () => {
			invalidate();
			goToNextPending();
		},
		onError: handleError,
	});
	const rejectMutation = useMutation({
		mutationFn: () => rejectVerkoop(id),
		onSuccess: () => {
			invalidate();
			goToNextPending();
		},
	});
	const reopenMutation = useMutation({
		mutationFn: () => reopenVerkoop(id),
		onSuccess: () => invalidate(),
		onError: handleError,
	});
	const deleteMutation = useMutation({
		mutationFn: () => deleteDocument(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['verkoopfacturen'] });
			queryClient.invalidateQueries({ queryKey: ['documents'] });
			navigate({ to: '/verkoopfacturen' });
		},
		onError: handleError,
	});

	function handleError(err: unknown) {
		const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
		setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
	}

	if (docQuery.isLoading || meQuery.isLoading) {
		return <div className="p-12 text-center text-sm text-muted-foreground">Laden…</div>;
	}
	if (!doc) {
		return (
			<div className="mx-auto max-w-md p-12 text-center text-sm">
				<AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
				Verkoopfactuur niet gevonden of geen toegang.
			</div>
		);
	}

	const status = doc.exact_status ?? 'pending_review';
	const isLocked = status !== 'pending_review';
	const canApprove = meQuery.data?.can_approve_exact === true;
	const line = draft?.lines[0];
	const isDirty = Boolean(draft && JSON.stringify(draft) !== JSON.stringify(proposal));

	// Totaal incl BTW = excl + vat (client-side berekend).
	const excl = Number(line?.amount_excl_vat ?? '0');
	const vat = Number(line?.vat_amount ?? '0');
	const totaalIncl = Number.isFinite(excl) && Number.isFinite(vat) ? (excl + vat).toFixed(2) : null;

	const updateHeader = (patch: Partial<VerkoopProposal['header']>) => {
		setDraft((d) => (d ? { ...d, header: { ...d.header, ...patch } } : d));
	};
	const updateLine = (patch: Partial<VerkoopProposal['lines'][number]>) => {
		setDraft((d) => (d && d.lines[0] ? { ...d, lines: [{ ...d.lines[0], ...patch }, ...d.lines.slice(1)] } : d));
	};

	return (
		<div className="space-y-4">
			{/* Page-header met titel + actie-knoppen rechts (mirror van inkoop-detail) */}
			<div className="flex flex-wrap items-start gap-3">
				<Button variant="ghost" size="sm" asChild>
					<Link to="/verkoopfacturen">
						<ArrowLeft className="mr-1.5 h-4 w-4" />
						Terug
					</Link>
				</Button>
				<div className="flex-1 min-w-0">
					<h1 className="truncate text-xl font-semibold">
						{(doc.counterparty_name as string) ?? 'Verkoopfactuur'}
					</h1>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{doc.entity_name} · {(doc.original_filename as string | undefined) ?? id} ·{' '}
						{totaalIncl ? `€ ${totaalIncl} incl.` : '—'}
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{draft?.header.is_credit_note && (
						<Badge variant="muted" className="bg-amber-100 text-amber-900">
							Creditfactuur
						</Badge>
					)}
					<Badge variant="muted">
						{EXACT_STATUS_LABELS[status as keyof typeof EXACT_STATUS_LABELS] ?? status}
					</Badge>
					{!isLocked && isDirty && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => draft && patchMutation.mutate(draft)}
							disabled={patchMutation.isPending}
						>
							Opslaan
						</Button>
					)}
					{!isLocked && canApprove && (
						<Button
							size="sm"
							onClick={() => approveMutation.mutate()}
							disabled={approveMutation.isPending || !draft?.header.customer_exact_id}
						>
							<CheckCircle2 className="mr-1.5 h-4 w-4" />
							Goedkeuren & boeken
						</Button>
					)}
					{!isLocked && canApprove && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => rejectMutation.mutate()}
							disabled={rejectMutation.isPending}
						>
							Afkeuren
						</Button>
					)}
					{(status === 'not_applicable' || status === 'failed') && canApprove && (
						<Button
							variant="outline"
							size="sm"
							onClick={() => reopenMutation.mutate()}
							disabled={reopenMutation.isPending}
						>
							<ArrowLeft className="mr-1.5 h-4 w-4 rotate-180" />
							Heropenen
						</Button>
					)}
					<Button
						variant="ghost"
						size="sm"
						onClick={() => {
							if (confirm('Document definitief verwijderen?')) deleteMutation.mutate();
						}}
						disabled={deleteMutation.isPending || status === 'sent' || status === 'already_present'}
						className="text-destructive hover:text-destructive"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			</div>

			{(status === 'sent' || status === 'already_present') && Boolean(doc.exact_entry_id) && (
				<VerkoopBookedBanner
					entryId={doc.exact_entry_id as string}
					alreadyPresent={status === 'already_present'}
					paymentStatus={(doc.payment_status as ExactPaymentStatus | null) ?? null}
					paymentDate={(doc.payment_date as string | null) ?? null}
					paymentSyncedAt={(doc.payment_status_synced_at as string | null) ?? null}
				/>
			)}

			{doc.exact_error_reason && status === 'failed' && (
				<Card className="border-destructive/40 bg-destructive/5">
					<CardContent className="p-3 text-sm text-destructive">
						<strong>Fout bij boeken:</strong> {doc.exact_error_reason as string}
					</CardContent>
				</Card>
			)}

			{error && (
				<Card className="border-destructive/40 bg-destructive/5">
					<CardContent className="flex items-start justify-between gap-2 p-3 text-sm text-destructive">
						<span className="break-all">{error}</span>
						<button type="button" onClick={() => setError(null)} aria-label="Sluit">
							<X className="h-4 w-4" />
						</button>
					</CardContent>
				</Card>
			)}

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
				<div className="space-y-4">
					{/* Header-card */}
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Factuurgegevens</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="grid grid-cols-2 gap-3">
								<div className="space-y-1">
									<Label>Factuurnummer</Label>
									<Input
										value={draft?.header.factuurnummer ?? ''}
										onChange={(e) => updateHeader({ factuurnummer: e.target.value || null })}
										disabled={isLocked}
										placeholder="2025…"
									/>
								</div>
								<div className="space-y-1">
									<Label>Factuurdatum</Label>
									<Input
										type="date"
										value={draft?.header.factuurdatum ?? ''}
										onChange={(e) => updateHeader({ factuurdatum: e.target.value || null })}
										disabled={isLocked}
									/>
								</div>
								<div className="space-y-1">
									<Label>Vervaldatum</Label>
									<Input
										type="date"
										value={draft?.header.vervaldatum ?? ''}
										onChange={(e) => updateHeader({ vervaldatum: e.target.value || null })}
										disabled={isLocked}
									/>
								</div>
								<div className="space-y-1">
									<Label>Klantnummer (Exact)</Label>
									<Input value={draft?.header.customer_code ?? '—'} disabled />
								</div>
							</div>

							<CustomerPicker
								customers={customersQuery.data?.items ?? []}
								selectedExactId={draft?.header.customer_exact_id ?? null}
								selectedName={draft?.header.customer_name ?? null}
								disabled={isLocked}
								onSelect={(c) =>
									updateHeader({
										customer_exact_id: c?.exact_id ?? null,
										customer_name: c?.name ?? null,
										customer_code: c?.code ?? null,
									})
								}
							/>

							<div className="space-y-1">
								<Label>Omschrijving (boeking)</Label>
								<Input
									value={draft?.header.description ?? ''}
									onChange={(e) => updateHeader({ description: e.target.value })}
									disabled={isLocked}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Regel-card (één regel voor verkoop) */}
					<Card>
						<CardHeader>
							<CardTitle className="text-base">Boekingsregel</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							{line ? (
								<>
									<div className="space-y-1">
										<Label>Omschrijving</Label>
										<Input
											value={line.description}
											onChange={(e) => updateLine({ description: e.target.value })}
											disabled={isLocked}
										/>
									</div>
									<div className="grid grid-cols-3 gap-3">
										<div className="space-y-1">
											<Label>Grootboek</Label>
											<Input value={line.ledger_account ?? ''} disabled />
										</div>
										<div className="space-y-1">
											<Label>BTW-code</Label>
											<Input value={line.vat_code ?? ''} disabled />
										</div>
										<div className="space-y-1">
											<Label>BTW %</Label>
											<Input value={line.vat_percent ?? ''} disabled />
										</div>
									</div>
									<div className="grid grid-cols-3 gap-3">
										<div className="space-y-1">
											<Label>Excl. BTW</Label>
											<Input
												value={line.amount_excl_vat ?? ''}
												onChange={(e) => updateLine({ amount_excl_vat: e.target.value || null })}
												disabled={isLocked}
												className="font-mono"
											/>
										</div>
										<div className="space-y-1">
											<Label>BTW-bedrag</Label>
											<Input
												value={line.vat_amount ?? ''}
												onChange={(e) => updateLine({ vat_amount: e.target.value || null })}
												disabled={isLocked}
												className="font-mono"
											/>
										</div>
										<div className="space-y-1">
											<Label>Totaal incl. BTW</Label>
											<Input
												value={totaalIncl ?? ''}
												disabled
												className="font-mono font-semibold"
											/>
										</div>
									</div>
								</>
							) : (
								<p className="text-sm text-muted-foreground">Geen regels in voorstel.</p>
							)}
						</CardContent>
					</Card>

					<p className="text-xs text-muted-foreground">
						{formatDateISO((doc.created_at as string) ?? '')} · {doc.id as string}
					</p>
				</div>

				{/* PDF preview rechts */}
				<Card className="overflow-hidden">
					<CardHeader className="flex flex-row items-center justify-between space-y-0">
						<CardTitle className="text-base">PDF</CardTitle>
						<Button variant="ghost" size="sm" asChild>
							<a href={documentPdfUrl(id)} target="_blank" rel="noopener noreferrer">
								<ExternalLink className="mr-1.5 h-4 w-4" />
								Open in tab
							</a>
						</Button>
					</CardHeader>
					<CardContent className="p-0">
						{doc.mime_type === 'application/pdf' ? (
							<iframe
								title="PDF preview"
								src={documentPdfUrl(id)}
								className="h-[80vh] w-full border-0"
							/>
						) : (
							<div className="p-6 text-sm text-muted-foreground">Geen PDF-preview beschikbaar.</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

/**
 * Customer-picker: input voor zoeken (substring op naam OF code), met
 * gefilterde dropdown van resultaten. Beter UX dan een 46-item-Select bij
 * IHB-omvang en groter voor entities met 100+ klanten.
 */
function CustomerPicker({
	customers,
	selectedExactId,
	selectedName,
	disabled,
	onSelect,
}: {
	customers: ExactCustomer[];
	selectedExactId: string | null;
	selectedName: string | null;
	disabled: boolean;
	onSelect: (c: ExactCustomer | null) => void;
}) {
	const [search, setSearch] = useState('');
	const [open, setOpen] = useState(false);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return customers.slice(0, 20);
		return customers
			.filter((c) => {
				const name = c.name.toLowerCase();
				const code = (c.code ?? '').toLowerCase();
				return name.includes(q) || code.includes(q);
			})
			.slice(0, 20);
	}, [customers, search]);

	if (selectedExactId && selectedName && !open) {
		return (
			<div className="space-y-1">
				<Label>Klant</Label>
				<div className="flex items-center gap-2">
					<div className="flex-1 rounded-md border bg-muted/30 px-3 py-2 text-sm">
						{selectedName}
					</div>
					{!disabled && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => {
								onSelect(null);
								setOpen(true);
							}}
						>
							Wijzig
						</Button>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-1">
			<Label>Klant</Label>
			<Input
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				placeholder="Zoek op naam of klantnummer…"
				disabled={disabled}
				autoFocus={open}
			/>
			{filtered.length > 0 ? (
				<div className="mt-1 max-h-60 overflow-y-auto rounded-md border bg-background">
					{filtered.map((c) => (
						<button
							key={c.exact_id}
							type="button"
							className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted/50"
							onClick={() => {
								onSelect(c);
								setSearch('');
								setOpen(false);
							}}
						>
							<span className="truncate">{c.name}</span>
							{c.code && <span className="shrink-0 text-xs text-muted-foreground">{c.code}</span>}
						</button>
					))}
				</div>
			) : (
				search && <p className="text-xs text-muted-foreground">Geen klant gevonden.</p>
			)}
			{!selectedExactId && (
				<p className="text-xs text-destructive">
					Klant niet automatisch gematched. Kies handmatig vóór goedkeuren.
				</p>
			)}
		</div>
	);
}


function VerkoopBookedBanner({
	entryId,
	alreadyPresent,
	paymentStatus,
	paymentDate,
	paymentSyncedAt,
}: {
	entryId: string;
	alreadyPresent: boolean;
	paymentStatus: ExactPaymentStatus | null;
	paymentDate: string | null;
	paymentSyncedAt: string | null;
}) {
	const [copied, setCopied] = useState(false);
	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(entryId);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// no-op
		}
	};
	return (
		<div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
			<CheckCircle2 className="h-4 w-4" />
			<span>
				{alreadyPresent
					? 'Deze factuur stond al in Exact (idempotent overgeslagen).'
					: 'Geboekt in Exact als verkoopboeking.'}
			</span>
			{paymentStatus && (
				<span
					className="inline-flex items-center gap-1.5"
					title={paymentSyncedAt ? `Bijgewerkt: ${paymentSyncedAt}` : undefined}
				>
					<span className={`h-2 w-2 rounded-full ${PAYMENT_STATUS_DOT[paymentStatus]}`} aria-hidden />
					<span>
						{PAYMENT_STATUS_LABELS[paymentStatus]}
						{paymentStatus === 'paid' && paymentDate && ` op ${formatDateISO(paymentDate)}`}
					</span>
				</span>
			)}
			<span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px]">
				<span className="text-emerald-700">EntryID</span>
				<span>{entryId}</span>
				<button
					type="button"
					onClick={onCopy}
					className="rounded p-1 text-emerald-700 hover:bg-emerald-100"
					title="Kopieer EntryID"
				>
					<Copy className="h-3 w-3" />
				</button>
				{copied && <span className="text-emerald-700">gekopieerd</span>}
			</span>
		</div>
	);
}
